import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Link, useHistory } from "react-router-dom";
import { Trans } from "@lingui/macro";

import { FLOW_STATUS } from "base/constants";

import {
  StatusChip,
  STATUS_CHIP_COLOR,
  STATUS_CHIP_TYPE
} from "../../datadisplay/chip";
import { UserIconList } from "@/datadisplay/usericonlist";
import { OverflowText } from "@/datadisplay/overflowtext";
import { Tooltip, TOOLTIP_POSITION } from "../../feedback/tooltip";
import { Icon, ICON_TYPES } from "@/common/components/primitives/icon";
import { FlowIcon } from "@/common/components/primitives/flowicon";
import {
  BaseDropdown,
  BASE_DROPDOWN_TRIGGER,
  BASE_DROPDOWN_POSITION
} from "../../inputs/dropdown";
import { MultiLine } from "@/inputs/multiline";
import {
  AddMemberIcon,
  BackArrowIcon,
  StarIcon,
  ArrowTopPointedIcon,
  ArrowRightPointedIcon,
  StarFilledIcon
} from "../../icons";

import styles from "./leftnav.css";

const context = React.createContext();
// eslint-disable-next-line kf/const-naming-convention
export const useFlowNav = function useFlowNav() {
  return React.useContext(context);
};

const ACTION = {
  TOGGLE_NAV: "ToggleNav",
  TOGGLE_OPEN: "ToggleOpen",
  REFRESH_FLOW: "RefreshFlow",
  UPDATE_FLOW: "UpdateFlow"
};

function reducer(state, action) {
  switch (action.type) {
    case ACTION.TOGGLE_NAV:
      // holds the persistance state of nav
      return {
        ...state,
        isNavCollapsed: !state.isNavCollapsed,
        isNavOpen: false
      };
    case ACTION.TOGGLE_OPEN:
      //holds the volatile state of nav (when collapsed, mouse hover will open the nav)
      return { ...state, isNavOpen: action.payload };
    case ACTION.UPDATE_FLOW:
      return { ...state, ...action.payload };
    case ACTION.REFRESH_FLOW:
      return { ...state, expandNav: false };
    default:
      return state;
  }
}

export function FlowNavContainer(props) {
  FlowNavContainer.propTypes = {
    width: PropTypes.string
  };

  let [state, dispatch] = React.useReducer(reducer, {}, function initlizer() {
    return {
      isNavCollapsed: Boolean(localStorage.getItem("KF::isNavCollapsed")),
      isNavOpen: Boolean(localStorage.getItem("KF::isNavCollapsed"))
    };
  });

  let [value, setValue] = React.useState({
    navActions: ACTION,
    navDispatch: dispatch,
    navState: state
  });

  let rootElemRef = React.useRef();

  let leftNavWidth = state.isNavCollapsed ? "5.6rem" : props.width || "25.6rem";

  React.useLayoutEffect(
    function setstyle() {
      rootElemRef.current?.style.setProperty("--leftNavWidth", leftNavWidth);
      rootElemRef.current?.style.setProperty(
        "--leftNavExpandedWidth",
        props.width || "25.6rem"
      );
    },
    [rootElemRef, leftNavWidth, props.width]
  );

  React.useEffect(
    function updateState() {
      setValue((oldState) => ({ ...oldState, navState: state }));
    },
    [state]
  );

  return (
    <context.Provider value={value}>
      <div ref={rootElemRef} className={styles.flowNavContainer} {...props} />
    </context.Provider>
  );
}

export function LeftNav(props) {
  let allChildrens = React.Children.toArray(props.children);
  let flowDetailsComp = allChildrens[0];
  let allNavItems = allChildrens.slice(1, allChildrens.length);

  let { navState, navDispatch } = useFlowNav();
  let [classNames, setClassNames] = React.useState(
    navState.isNavCollapsed ? styles.collapsedNav : ""
  );

  React.useEffect(
    function toggleClass() {
      if (navState.isNavCollapsed) {
        setClassNames(`${styles.shrinkNav}`);
      } else {
        setClassNames("");
      }
    },
    [navState.isNavCollapsed]
  );

  let timerRef = React.useRef();
  const onMouseEnter = (e) => {
    if (navState.isNavCollapsed) {
      timerRef.current = setTimeout(function delayExpantion() {
        setClassNames(`${styles.expandedNav} shadow-12dp`);
        navDispatch({
          type: ACTION.TOGGLE_OPEN,
          payload: true
        });
      }, 500);
    }
  };

  const onMouseLeave = () => {
    if (navState.isNavCollapsed) {
      clearTimeout(timerRef.current);
      setClassNames(`${styles.shrinkNav}`);
      navDispatch({
        type: ACTION.TOGGLE_OPEN,
        payload: false
      });
    }
  };

  return (
    <React.Fragment>
      <div
        className={`${styles.leftNav} ${props.className} ${classNames}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {flowDetailsComp}
        <div className="leftNavItemsContainer">{allNavItems}</div>
      </div>
      <div className={styles.leftNavPlaceholder} />
    </React.Fragment>
  );
}

LeftNav.propTypes = {
  children: PropTypes.array,
  className: PropTypes.string
};

export function FlowDetails(props) {
  let readOnly = props.readOnly || props.disabled;
  let flowNameBoxRef = React.useRef();
  let { navState, navDispatch, navActions } = useFlowNav();
  // debugger;

  useEffect(function navCollapse() {
    if (props.leftNavState)
      navDispatch({
        type: navActions.UPDATE_FLOW,
        payload: {
          isNavCollapsed: props.leftNavState.isNavCollapsed,
          isNavOpen: props.leftNavState.isNavOpen
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`${styles.flowDetails} ${props.className}`}>
      <Link to={props.homeLink} className={styles.flowIcon}>
        <FlowIcon type={props.type.toLowerCase()} height={40} width={40} />
      </Link>

      <Icon
        className={`${styles.drawerIcon} ${
          navState.isNavCollapsed ? "" : "rotate-180"
        }`}
        onClick={() => {
          if (props.updateLeftNavState) {
            props.updateLeftNavState({
              isNavCollapsed: !navState.isNavCollapsed,
              isNavOpen: false
            });
          }

          navDispatch({
            type: navActions.TOGGLE_NAV
          });
        }}
        clickable={true}
        paddingNone={true}
      >
        <ArrowRightPointedIcon />
      </Icon>

      <div className={styles.flowInfo}>
        <div className={styles.flowNameBox} ref={flowNameBoxRef}>
          <FlowNameEditor
            style={{
              width: `${flowNameBoxRef.current?.width || 14}rem`
            }}
            name={props.name}
            onChange={props.onChange}
            readOnly={readOnly}
          />
          {props.hasFavourite && (
            <Icon
              className={styles.starIcon}
              paddingNone={true}
              highlight={false}
              onClick={() => props.onFavourite(!props.isFavourite)}
            >
              {props.isFavourite ? <StarFilledIcon /> : <StarIcon />}
            </Icon>
          )}
        </div>
        {!props.disabled && (
          <div className={styles.flowSharedWith}>
            <Tooltip
              position={TOOLTIP_POSITION.BOTTOM}
              content={<Trans>View all members</Trans>}
            >
              <Link to={props.membersLink}>
                <UserIconList
                  size="tiny"
                  count={props.users.length}
                  users={[...props.users] || []}
                />
              </Link>
            </Tooltip>

            {!readOnly && (
              <Link
                id={"inviteMember"}
                className={styles.inviteUser}
                to={props.inviteLink}
              >
                <Icon
                  paddingNone={true}
                  clickable={true}
                  type={ICON_TYPES.TYPE3}
                >
                  <AddMemberIcon />
                </Icon>
                <p className="micro">
                  <Trans>Invite</Trans>
                </p>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

FlowDetails.propTypes = {
  name: PropTypes.string,
  type: PropTypes.string,
  users: PropTypes.array,
  hasFavourite: PropTypes.bool,
  isFavourite: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  homeLink: PropTypes.string,
  membersLink: PropTypes.string,
  inviteLink: PropTypes.string,
  onChange: PropTypes.func,
  onFavourite: PropTypes.func,
  updateLeftNavState: PropTypes.func,
  leftNavState: PropTypes.object,
  className: PropTypes.string
};

FlowDetails.defaultProps = {
  type: "",
  users: [],
  onChange: () => {},
  onFavourite: () => {}
};

function FlowNameEditor({ name, onChange, style, readOnly }) {
  let [flowName, setFlowName] = React.useState(name);

  const onKeyDown = (e, closePopup) => {
    if (e.key === "Enter") {
      onBlur(e);
      closePopup();
    }
    if (e.key === "Escape") {
      closePopup();
    }
  };

  const onBlur = (e) => {
    if (e.target.value.trim()) {
      onChange(e.target.value);
    }
  };

  const onNameChange = (evt) => {
    setFlowName(evt.target.value);
  };

  return readOnly ? (
    <div className={`${styles.flowName} ${styles.readOnly}`} style={style}>
      <OverflowText className={`base-600`}>{flowName}</OverflowText>
    </div>
  ) : (
    <BaseDropdown
      trigger={BASE_DROPDOWN_TRIGGER.CLICK}
      position={BASE_DROPDOWN_POSITION.BOTTOM_LEFT_OVER}
      targetRenderer={() => {
        return (
          <div className={styles.flowName} style={style}>
            <OverflowText className={`base-600`}>{flowName}</OverflowText>
          </div>
        );
      }}
      listRenderer={({ closePopup }) => {
        return (
          <div className={styles.flowNameEditor}>
            <MultiLine
              value={flowName}
              autoFocus={true}
              autoGrow={true}
              className="base-600"
              onBlur={onBlur}
              onKeyDown={(e) => onKeyDown(e, closePopup)}
              onChange={onNameChange}
            />
            <p className={`${styles.hint} small`}>
              <Trans>
                Press <b>Enter</b> to update
              </Trans>
            </p>
          </div>
        );
      }}
    />
  );
}

FlowNameEditor.propTypes = {
  name: PropTypes.string,
  readOnly: PropTypes.string,
  onChange: PropTypes.func,
  style: PropTypes.object
};

export function NavItem({ active, className = "", ...props }) {
  let Wrapper = props.routeLink
    ? Link
    : ({ children }) => <React.Fragment>{children}</React.Fragment>;

  return (
    <Wrapper to={props.routeLink}>
      <div
        tabIndex="0"
        {...props}
        className={`base-400 ${styles.navItem} ${className} ${
          active ? "activeNavItem" : ""
        }`}
      />
    </Wrapper>
  );
}

NavItem.propTypes = {
  active: PropTypes.bool,
  routeLink: PropTypes.string,
  className: PropTypes.string
};

NavItem.defaultProps = {
  routeLink: ""
};

function NavItemIcon({
  className = "",
  children,
  active,
  activeIcon: ActiveIcon,
  ...props
}) {
  return (
    <Icon
      paddingNone={true}
      {...props}
      className={`${styles.navItemIcon} ${className}`}
    >
      {active ? ActiveIcon : children}
    </Icon>
  );
}

NavItemIcon.propTypes = {
  className: PropTypes.string,
  active: PropTypes.bool,
  activeIcon: PropTypes.element,
  children: PropTypes.element
};

function NavItemTitle({ className = "", ...props }) {
  return (
    <div
      {...props}
      className={`ellipsis ${styles.navItemTitle} ${className}`}
    />
  );
}

NavItemTitle.propTypes = {
  className: PropTypes.string
};

export function NavItemDivider() {
  return <div className={styles.divider} />;
}

NavItem.Icon = NavItemIcon;
NavItem.Title = NavItemTitle;
NavItem.Divider = NavItemDivider;

export function NavMenu(props) {
  let [isMenuOpen, toggleMenu] = React.useState(null);
  let history = useHistory();

  let allChildren = React.Children.toArray(props.children);
  let NavMenuHeader = React.cloneElement(allChildren[0], {
    onClick: () => {
      props.setViewLclStrg &&
        props.setViewLclStrg({ isViewCollapsed: !isMenuOpen || "" });
      toggleMenu(!isMenuOpen);
      props.routelink && !isMenuOpen && history.push(props.routelink);
    },
    isMenuOpen
  });
  let NavItems = allChildren.slice(1);

  React.useEffect(
    function autoOpenFirstTime() {
      if (isMenuOpen === null) {
        toggleMenu(props.open);
      }
    },
    [props.open, isMenuOpen]
  );

  return (
    <div {...props} className={styles.navMenuContainer}>
      {NavMenuHeader}
      {isMenuOpen && (
        <div className={`${styles.navMenuItemsContainer} ${styles.open}`}>
          {NavItems}
        </div>
      )}
    </div>
  );
}

NavMenu.propTypes = {
  children: PropTypes.array,
  open: PropTypes.bool,
  setViewLclStrg: PropTypes.func,
  routelink: PropTypes.string
};

function NavMenuHeader(props) {
  return (
    <div
      tabIndex="0"
      className={`${styles.navMenuHeader} base-400`}
      onClick={props.onClick}
    >
      {props.children}
      <Icon
        className={`${styles.icon} ${props.isMenuOpen ? "" : "rotate-180"}`}
        paddingNone={true}
      >
        <ArrowTopPointedIcon />
      </Icon>
    </div>
  );
}

NavMenuHeader.propTypes = {
  onClick: PropTypes.func,
  isMenuOpen: PropTypes.bool,
  children: PropTypes.array
};

NavMenu.Header = NavMenuHeader;

export function NavMore(props) {
  let [isOpen, toggleMore] = React.useState(false);

  return (
    <React.Fragment>
      {!isOpen && (
        <NavItem
          className={`${styles.navMore}`}
          onClick={() => toggleMore(!isOpen)}
          // data-animation-name="fadein"
          // data-animation-duration="0.5x"
        >
          <NavItem.Icon className={`${styles.icon} rotate-180`}>
            <ArrowTopPointedIcon />
          </NavItem.Icon>
          <NavItem.Title className={`small ${styles.linkText}`}>
            More
          </NavItem.Title>
        </NavItem>
      )}
      {isOpen && (
        <React.Fragment>
          <div className={`${styles.navMoreItems} ${styles.open}`}>
            {props.children}
          </div>
          <NavItem
            className={styles.navMore}
            onClick={() => toggleMore(!isOpen)}
            // data-animation-name="flyin"
            // data-animation-duration="0.5x"
          >
            <NavItem.Icon className={styles.icon}>
              <ArrowTopPointedIcon />
            </NavItem.Icon>
            <NavItem.Title className={`small ${styles.linkText}`}>
              Less
            </NavItem.Title>
          </NavItem>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

NavMore.propTypes = {
  children: PropTypes.node
};
export function SubNav({ id, title, ...props }) {
  let [navChild, subNavChildren] = React.Children.toArray(props.children);
  let [openSubNav, toggleSubNav] = React.useState(null);

  React.useEffect(
    function autoOpenFirstTime() {
      if (openSubNav === null) {
        toggleSubNav(props.open);
      }
    },
    [props.open, openSubNav]
  );

  let srcClickCb = navChild.props.onClick;
  navChild = React.cloneElement(navChild, {
    onClick: () => {
      toggleSubNav((isOpen) => !isOpen);
      srcClickCb?.();
    }
  });

  return !openSubNav ? (
    navChild
  ) : (
    <div className={`${styles.subNavItemsContainer} ${styles.open}`}>
      <NavItem
        onClick={() => {
          toggleSubNav(!openSubNav);
          props.onBackClick();
        }}
        clickable={true}
      >
        <NavItem.Icon>
          <BackArrowIcon />
        </NavItem.Icon>
        <NavItem.Title>{title}</NavItem.Title>
      </NavItem>
      <div className={styles.subNavItems} id={id}>
        {subNavChildren}
      </div>
    </div>
  );
}

SubNav.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  onBackClick: PropTypes.func,
  children: PropTypes.node,
  open: PropTypes.bool
};

SubNav.defaultProps = {
  onBackClick: () => {}
};

SubNav.Items = function SubNavItems(props) {
  return props.children;
};

export const FlowContainer = React.memo(function FlowContainer(props) {
  return <div {...props} className={styles.flowContainer} />;
});

export function FlowBanner({ name, type, status, ...restProps }) {
  return (
    <div className={styles.flowBanner}>
      <div className={styles.flowHeader}>
        <FlowIcon type={type?.toLowerCase()} />
        <p className="h5-600">{name}</p>
        {status === FLOW_STATUS.ARCHIVED && (
          <StatusChip
            color={STATUS_CHIP_COLOR.RED}
            type={STATUS_CHIP_TYPE.LITE}
          >
            <Trans>Archived</Trans>
          </StatusChip>
        )}
      </div>
      <div {...restProps} />
    </div>
  );
}

FlowBanner.propTypes = {
  name: PropTypes.string,
  type: PropTypes.string,
  status: PropTypes.string
};
